/**
 * Tag name for the custom swagger blocks.
 */
export enum CustomSwaggerBlock {
    description = 'swagger-description',
    parameter = 'swagger-parameter',
    response = 'swagger-response',
}

/**
 * Set of attributes that are case sensitive but don't need to be stringified
 * as they are supported by the parser.
 */
const LEGACY_CASE_SENSITIVE_ATTRIBUTES = new Set([
    // Used in Swagger/OpenAPI blocks
    'baseUrl',
    // Used in Embed and Swagger/OpenAPI blocks
    'fullWidth',
    // Used in code blocks
    'lineNumbers',
]);

/**
 * Transform a key:value map into a map of data properties.
 * The keys that are case sensitive or include dashes doesn't convert properly
 * to and from HTML `data-` attributes. To prevent losing information, we store
 * them in a separate object and stringify them in the HTML.
 * Only the `LEGACY_CASE_SENSITIVE_ATTRIBUTES` are kept unmodified as they are supported
 * otherwise for existing cases (embeds and swagger).
 *
 * e.g. { hello: 'world' } => { 'data-hello': 'world' }
 * e.g. { foo: ['bar', 'baz'] } => { 'data-foo-0': 'bar', 'data-foo-1': 'baz' }
 * e.g. { 'Foo-Bar': 'baz' } => { 'data-gitbook-props': '{"Foo-Bar":"baz"}' }
 */
export function createCustomBlockProperties(data: object): { [key: string]: string } {
    const encodedData = Object.keys(data).reduce(
        (result, key) => {
            if (key === 'className') {
                result.className = data[key];
            } else if (LEGACY_CASE_SENSITIVE_ATTRIBUTES.has(key)) {
                result[`data-${key}`] = data[key];
            } else if (key.toLowerCase() !== key || key.includes('-')) {
                if (!result.dataGitbookProps) {
                    result.dataGitbookProps = {};
                }

                result.dataGitbookProps[key] = data[key];
            } else if (Array.isArray(data[key])) {
                // encode arrays
                data[key].forEach((el, i) => {
                    result[`data-${key}-${i}`] = el;
                });
            } else {
                result[`data-${key}`] = data[key];
            }
            return result;
        },
        {} as { [key: string]: string | string[] | object }
    );

    return {
        ...encodedData,
        ...(encodedData.dataGitbookProps
            ? { dataGitbookProps: JSON.stringify(encodedData.dataGitbookProps) }
            : {}),
    };
}
